import $ from "cash-dom";
import Popup from './popup';

let instance = null;

/** Breathing room kept between a repositioned tooltip and the edge of the screen. */
const EDGE_GUTTER = 10;

class Tooltips {
    constructor() {
        if (!instance) {
            this._isInited = null;

            instance = this;
        }

        return instance;
    }

    /**
     * @return {Tooltips}
     */
    static getInstance() {
        if (!Tooltips._instance) {
            Tooltips._instance = new Tooltips();
        }

        return Tooltips._instance;
    }

    _getIsInited() {
        if (this._isInited === null) {
            const $body = $('body');
            const isInited = $body.length ? $body.data('tooltip-is-inited') : null;
            this._isInited = isInited === undefined || isInited === null ? null : !!Number.parseFloat(isInited);
        }

        return this._isInited;
    }

    _setIsInited() {
        $('body').data('tooltip-is-inited', 1);
    }

    /**
     * Prefer HTML attribute — cash-dom/jQuery.cache may omit large HTML from data-tooltip.
     */
    _getTooltipHtml(el) {
        const $el = $(el);
        let html = $el.attr('data-tooltip');
        if (html !== undefined && html !== null && String(html).length > 0) {
            return String(html);
        }
        const fromData = $el.data('tooltip');
        if (fromData !== undefined && fromData !== null && String(fromData).length > 0) {
            return String(fromData);
        }
        return '';
    }

    show(el) {
        var self = this,
            position = self.getPosition(el),
            tooltip = self.build(el),
            id = this._id();

        //apply id to link control and tooltip
        el.data("tooltip-id", id);
        tooltip.attr("data-id", id);

        if (tooltip.length > 0) {
            tooltip.css({ top: 0, left: 0, display: 'block', opacity: 1 });
            $(document.body).append(tooltip);

            const pos = self.getPositionData(el);

            let posWidthCorrector = el.attr('data-tooltip-pos-width-corrector') || el.data('tooltip-pos-width-corrector');
            posWidthCorrector = posWidthCorrector ? Number.parseInt(posWidthCorrector) : 0;
            const posWidth = el.hasClass('without-pos-width') ? posWidthCorrector : pos.width / 2;

            var actualWidth = tooltip.width(),
                actualHeight = tooltip.height(),
                tp;

            switch (position) {
                case 'bottom':
                    tp = { top: pos.top + pos.height, left: self._keepInViewport(tooltip, pos.left + pos.width / 2 - actualWidth / 2, pos.left + pos.width / 2) };
                    break;
                case 'bottom-end':
                    //legacy WizCase's disclosure popovers: under the trigger, flush with its end edge
                    //(the right one, the left in RTL), with no arrow
                    tooltip.find('.simple-tooltip-arrow').remove();
                    tp = self._bottomEnd(el, tooltip, pos);
                    break;
                case 'left':
                    tp = { top: pos.top + pos.height / 2 - actualHeight / 2, left: pos.left - actualWidth };
                    break;
                case 'right':
                    tp = { top: pos.top + pos.height / 2 - actualHeight / 2, left: pos.left + pos.width };
                    break;
                default: //top
                    tp = { top: pos.top - actualHeight - 20, left: self._keepInViewport(tooltip, pos.left + posWidth - actualWidth / 2, pos.left + posWidth) };
            }

            tooltip.css(tp);
        }
    }

	/** Excludes the scrollbar, unlike `$(window).width()`. */
	_viewportWidth() {
		return document.documentElement.clientWidth || $(window).width();
	}

	/**
	 * Pulls a tooltip that would overflow either side of the screen back inside it.
	 * Shrinking it instead left a narrow, very tall column next to a trigger near the edge,
	 * so it keeps its full width and the arrow is moved to keep pointing at the trigger.
	 */
	_keepInViewport(tooltip, left, anchorX) {
		//`tooltip.width()` is the content box; the edge is hit by the padded one
		const outerWidth = tooltip.get(0).offsetWidth,
			maxLeft = this._viewportWidth() - outerWidth - EDGE_GUTTER,
			clamped = Math.max(EDGE_GUTTER, Math.min(left, maxLeft));

		if (clamped !== left) {
			this._alignArrow(tooltip, anchorX - clamped);
		}

		return clamped;
	}

	/**
	 * The visible box (the tooltip less its own padding) starts 10px under the trigger and ends where the
	 * trigger ends, kept inside the screen.
	 */
	_bottomEnd(el, tooltip, pos) {
		const box = tooltip.get(0),
			style = window.getComputedStyle(box),
			padStart = Number.parseFloat(style.paddingLeft) || 0,
			padEnd = Number.parseFloat(style.paddingRight) || 0,
			padTop = Number.parseFloat(style.paddingTop) || 0,
			outerWidth = box.offsetWidth,
			rtl = window.getComputedStyle(el.get(0)).direction === 'rtl',
			minLeft = EDGE_GUTTER - padStart,
			maxLeft = this._viewportWidth() - EDGE_GUTTER - outerWidth + padEnd;

		let left = rtl ? pos.left - padStart : pos.left + pos.width - outerWidth + padEnd;
		left = Math.max(minLeft, Math.min(left, maxLeft));

		return { top: pos.top + pos.height + 10 - padTop, left: left };
	}

	/** Keeps the arrow under the trigger after the tooltip has been pulled off the edge. */
	_alignArrow(tooltip, offsetFromLeft) {
		//the arrow sits inside the content box, which is inset by the tooltip's own padding
		const content = tooltip.find('.simple-tooltip-content').get(0),
			inset = content ? content.offsetLeft : 0;

		tooltip.find('.simple-tooltip-arrow').css({ left: (offsetFromLeft - inset) + 'px' });
	}

	getPositionData(el) {
		const dom = el.get && el.get(0) ? el.get(0) : el[0];
		if (!dom || typeof dom.getBoundingClientRect !== 'function') {
			return { top: 0, left: 0, width: 0, height: 0 };
		}

		const rect = dom.getBoundingClientRect();
		const scrollTop = window.pageYOffset ?? document.documentElement.scrollTop ?? 0;
		const scrollLeft = window.pageXOffset ?? document.documentElement.scrollLeft ?? 0;

		const tooltipHeightGetter = el.find('.tooltip-height-getter').get(0);
		const height = tooltipHeightGetter ? tooltipHeightGetter.offsetHeight : rect.height;

		/* Document coordinates — cash-dom .offset() is unreliable vs jQuery; fixes mis-centered tooltips */
		return {
			top: rect.top + scrollTop,
			left: rect.left + scrollLeft,
			width: rect.width,
			height: height,
		};
	}

    getPosition(el) {
        var self = this,
            position = el.attr('data-tooltip-position') || 'top',
            position_lg = el.attr('data-tooltip-position-lg') || position;

        if ($(window).width() >= 1500) {
            position = position_lg;
        }

        return position;
    }

    build(el) {
        var self = this,
            content = self._getTooltipHtml(el),
            position = self.getPosition(el),
            is_thumb = false,
            additional_classes = '',
            data_classes = el.attr('data-tooltip-classes') || el.data('tooltip-classes');


        if (!content || !content.length) {
            return '';
        }

        if (/^http.+\.(jpg|png)$/.test(content)) {
            is_thumb = true;
            additional_classes = 'thumbnail loading';
            content = $('<img />').attr('src', content);

        }

        if (typeof data_classes !== 'undefined' && data_classes.length) {
            additional_classes += " " + data_classes + " ";
        }

        additional_classes += " " + 'tooltip-' + position;

        var $tooltip_content = $('<div />').attr({
            class: 'simple-tooltip-content'
        }).html(content).append('<div class="simple-tooltip-arrow"></div>');

        if (el.hasClass('closable')) {
            $tooltip_content.prepend('<div class="tooltip-close-container"><span class="tooltip-close">x</span></div>');
        }

        if (is_thumb) {
            $tooltip_content.append('<div class="img-loader light-loader"></div>');
        }

        var $tooltip = $('<div />').attr({
            class: 'simple-tooltip ' + additional_classes
        }).append($tooltip_content);


        if (is_thumb) {
            content.load(function () {
                $tooltip.removeClass('loading');
            });
        }

        return $tooltip;
    }

    initOnce() {
        if (this._getIsInited()) {
            return;
        }

        this._setIsInited();
        var self = this;

        $(document).on('mouseenter', '[data-tooltip]', function () {
            /* Align with theme md breakpoint (1023px): tablets need tap → popup path */
            if ($(this).attr('data-tooltip-trigger') === 'click' || $(window).width() <= 1023) {
                return;
            }

            self.show($(this));
        }).on('mouseleave', '[data-tooltip]', function (e) {
            if ($(this).attr('data-tooltip-trigger') === 'click' || $(window).width() <= 1023) {
                return;
            }

            const tooltipId = $(this).data('tooltip-id');

            if (tooltipId) {
                $('.simple-tooltip[data-id="' + tooltipId + '"]').remove();
            } else {
                $('.simple-tooltip').remove();
            }
        }).on('click', '[data-tooltip]', function (e) {
            const $host = $(this);

            if ($host.hasClass('prevent-default')) {
                e.preventDefault();
            }

            self._handleOutsideClickIfNeed($host);

            self.popup($host);
        }).on('click', '.tooltip-close', function (e) {
            $(this).closest('.simple-tooltip').remove();
        });

        $('[data-tooltip][data-tooltip-autoshow]').each(function () {
            var $host = $(this),
                timeout = $host.attr('data-tooltip-autoshow');

            if (timeout === '') timeout = 0;

            timeout = +timeout; //convert to Number

            setTimeout(function () {
                $host.trigger('mouseenter');
            }, timeout);

            setTimeout(function () {
                $host.trigger('mouseleave');
            }, timeout + 9000);
        });

        var window_width = $(window).width();
        $(window).on("resize", function () {
            if (window_width !== $(window).width()) {
                $('[data-tooltip]').first().trigger('mouseleave');
            }
        });
    }

    _handleOutsideClickIfNeed($host) {
        const self = this;

        if ($host.hasClass('close-on-outside-click') && !$host.hasClass('handler-added')) {
            const handler = (e) => {
                const $targetTooltip = $(e.target).closest('.simple-tooltip');

                if ($targetTooltip.get(0) !== self._getHostTooltip($host).get(0)
                    || !self._getHostTooltip($host).length
                    || $targetTooltip.get(0) === $host.get(0)) {

                    self._closeTooltip($host);
                    $(document).off('click', handler);
                    $host.removeClass('handler-added');
                }
            };

            $(document).on('click', handler);
            $host.addClass('handler-added');
        }
    }

    _getHostTooltip($host) {
        return $(".simple-tooltip[data-id=\"" + $host.data("tooltip-id") + "\"]");
    }

    _closeTooltip($host) {
        const $tooltip = this._getHostTooltip($host);

        if ($tooltip.length > 0) {
            $tooltip.remove();
        }
    }

    popup($el) {
        const showAsPopup = $el.hasClass('show-as-popup');

        if (showAsPopup) {
            this._showPopup($el);
            return;
        }

        const noMobilePopup = $el.hasClass('no-mobile-popup');

        if ($el.attr('data-tooltip-trigger') === 'click' && (noMobilePopup || $(window).width() > 1023)) {

            if ($(".simple-tooltip[data-id=\"" + $el.data("tooltip-id") + "\"]").length > 0) {
                $(".simple-tooltip[data-id=\"" + $el.data("tooltip-id") + "\"]").remove();
                return;
            } else {
                $(".simple-tooltip").remove();
            }

            this.show($el);
            return;
        }

        if ($(window).width() > 1023) {
            return;
        }

        this._showPopup($el);
    }

    _showPopup($el) {
        const icon = $('body').hasClass('design-template-33')
            ? '<i class="fas fa-circle-info"></i>'
            : '';

        const titleRaw = $el.attr('data-popup-title') || $el.data('popup-title');
        const title = titleRaw ? String(titleRaw) : $el.text();
        var content = "<div class=\"mobile-tooltip-content\">" +
            "<h3>" + icon + title + "</h3>" +
            (this._getTooltipHtml($el) || '') + "</div>";

        const popup = new Popup({ containerClass: 'tooltip' });
        popup.show(content);
    }

    _id() {
        return 't_' + Math.random().toString(36).substr(2, 9);
    }
}

export default Tooltips;
